<script setup lang='ts'>
import { ref, watch, computed, onMounted, onUnmounted, provide } from 'vue';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import FilterBody from '@/components/FilterBuilder/FilterBody.vue';
import { useQueryBuilder } from '@/composables/useQueryBuilder';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { ArrowPathIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/solid';
// The trigger sits in the list toolbars, which use outline icons only.
import { FunnelIcon } from '@heroicons/vue/24/outline';
import { filterRuleCountLabel } from '@/components/FilterBuilder/filterRuleCountLabel';
import type { IScope } from '@/types/filterBuilderTypes';
import { OPEN_RULE_COLUMN_KEY } from '@/components/FilterBuilder/openRuleColumn';
import { isSubmitShortcut } from '@/utils/submitShortcut';
import { LazyTooltip, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const emit = defineEmits<{
  applyFilter: [parsedQuery: IScope];
}>();

defineProps<{
  /**
   * When set, the editor renders as a full-screen modal below `md` instead of
   * an anchored popover. Used by the search toolbar's mobile tools dropdown so
   * tapping "Filter" opens a full modal.
   */
  fullscreen?: boolean;
  /**
   * Room-driven label control for the default trigger button (the search
   * toolbar's 3-state ladder decides how much fits — see toolbarFitState.ts):
   *   'always'   — always show the rule-count label (state 'full'; room
   *                decides, not viewport breakpoints),
   *   'iconOnly' — icon-only trigger; the label moves to title/aria-label
   *                (state 'icons'),
   *   'word'     — the funnel and the word "Filter", for a host that shows
   *                the rule count as its own badge (the list toolbar),
   *   undefined  — legacy breakpoint behavior (label hidden below md), for
   *                hosts that don't measure room.
   * A string mode (not a boolean) so an absent prop stays `undefined` —
   * Vue casts absent Boolean props to `false`, which would conflate the
   * legacy default with icon-only.
   */
  triggerLabelMode?: 'always' | 'iconOnly' | 'word';
}>();

const {
  filter,
  columnsForFilter,
} = usePersistentTable();

// Exposed as `v-model:open` so a host (the mobile tools menu) can react when
// the editor closes; defaults to uncontrolled for the standalone toolbar use.
const showFilterDialog = defineModel<boolean>('open', { default: false });
const isRunning = ref(false);
const { rawQuery, parseRawQuery, addRule, addFavoriteRule, addInnerRule, deleteRule, updateRules, initFilters } = useQueryBuilder();

const filterText = computed(() => filterRuleCountLabel(filter.value));

// Icon-only trigger: the label text moves into the tooltip and the aria-label,
// so the rule count stays discoverable. "2 rules" alone lacks context there,
// so it reads "Filter: 2 rules".
const iconOnlyTriggerTitle = computed(() =>
  filterText.value === 'Filter' ? 'Filter' : `Filter: ${filterText.value}`,
);

// Load the current filter whenever the builder opens. `immediate` covers a
// builder that mounts already open. That happens when the collapsed phone
// toolbar opens its tools menu and the builder in the same tick. Without it the
// builder showed no rules, and Apply saved an empty filter over the real one.
watch(
  showFilterDialog,
  (isOpen) => {
    if (isOpen) {
      initFilters(filter.value || { id: 'root', scopeType: 'and', rules: [] });
    }
  },
  { immediate: true },
);

const resetFilter = () => {
  isRunning.value = true;
  initFilters({ id: 'root', scopeType: 'and', rules: [] });
  isRunning.value = false;
};

const runFilter = async () => {
  isRunning.value = true;

  const parsedQuery = parseRawQuery();
  try {
    emit('applyFilter', parsedQuery);
  } finally {
    isRunning.value = false;
    showFilterDialog.value = false;
  }
};

// The row whose column list opens when it mounts. See openRuleColumn.ts.
const openRuleColumn = ref<string | null>(null);
provide(OPEN_RULE_COLUMN_KEY, openRuleColumn);

// Option+Enter adds a row at the top level, like "+ Add Filter", and opens its column list.
const addRuleAndOpenColumn = () => {
  openRuleColumn.value = addRule();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (isSubmitShortcut(event) && showFilterDialog.value && !isRunning.value) {
    event.preventDefault();
    runFilter();
  }
};

// Option+Enter listens in the capture phase, before the focused control sees the key, and stops
// the event there. Otherwise a focused column dropdown would also open, or an open one would pick
// its highlighted option.
const handleOptionEnter = (event: KeyboardEvent) => {
  if (
    event.key !== 'Enter' || !event.altKey || event.metaKey || event.ctrlKey || event.shiftKey ||
    !showFilterDialog.value || isRunning.value
  ) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  // Holding the keys down would otherwise add a row on every key repeat.
  if (!event.repeat) {
    addRuleAndOpenColumn();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('keydown', handleOptionEnter, true);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
  document.removeEventListener('keydown', handleOptionEnter, true);
});
</script>

<template>
  <!--
    Notion-style: anchor the filter UI to its trigger button via a Popover (no
    backdrop, no centered modal). The button itself is the trigger so the
    panel pops out aligned to its left edge.

    Saving and loading filters happens through saved views in the section
    sidebar (a saved view bundles the filter and the columns together), so this
    dialog is a pure editor. It edits the live filter and emits applyFilter on Apply.
  -->
  <!-- An icon-only funnel names itself and its rule count in a tooltip. The tooltip
       wraps the whole popover, not just its trigger. See LazyTooltip for why. -->
  <LazyTooltip :enabled="triggerLabelMode === 'iconOnly'">
    <Popover v-model:open="showFilterDialog">
      <PopoverTrigger as-child>
        <slot name="trigger">
          <Button
            type="button"
            variant="toolbar"
            size="sm"
            data-testid="filter-dialog-trigger"
            :class="triggerLabelMode === 'word' ? undefined : 'text-xs'"
            :aria-label="triggerLabelMode === 'iconOnly' || triggerLabelMode === 'word' ? iconOnlyTriggerTitle : undefined"
          >
            <FunnelIcon class="h-4 w-4" />
            <span v-if="triggerLabelMode === 'word'">Filter</span>
            <span
              v-else-if="triggerLabelMode !== 'iconOnly'"
              :class="triggerLabelMode === undefined ? 'hidden md:block' : undefined"
            >{{ filterText }}</span>
          </Button>
        </slot>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        :side-offset="6"
        class="w-fit md:min-w-[640px] max-w-[calc(100vw-24px)] max-h-[80vh] overflow-y-auto p-0"
        :class="fullscreen && 'popover-fullscreen max-md:!flex max-md:!flex-col max-md:!fixed max-md:!inset-0 max-md:!m-0 max-md:!max-h-none max-md:!max-w-none max-md:!w-screen max-md:!h-[calc(100dvh-var(--global-audio-player-height,0px))] max-md:!transform-none max-md:!rounded-none max-md:!border-0'"
      >
        <div class="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
          <span data-testid="filter-dialog-title" class="text-xs font-medium text-muted-foreground">
            Filter Builder
          </span>
          <button
            v-if="fullscreen"
            type="button"
            class="text-muted-foreground hover:text-foreground -mr-1 rounded p-1 md:hidden"
            data-testid="filter-dialog-close"
            aria-label="Close filter"
            @click="showFilterDialog = false"
          >
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>
        <div class="px-3 pb-2" :class="fullscreen && 'max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto'">
          <div v-if="isRunning" class="flex flex-col items-center justify-center gap-2 py-6" data-testid="filter-loading">
            <ArrowPathIcon class="w-6 h-6 animate-spin text-muted-foreground" />
            <span class="text-xs text-muted-foreground">Running…</span>
          </div>
          <FilterBody
            v-else
            data-testid="filter-body"
            v-model:scope="rawQuery"
            :columnDefinitions="columnsForFilter"
            :depth="1"
            @newRule="addRule"
            @newInnerRule="addInnerRule"
            @deleteRule="deleteRule"
            @insertFavoriteRule="addFavoriteRule"
            @updateRulesOrder="updateRules"
          />
        </div>
        <!--
          Footer: just the two rule-level commit actions. Saving, opening,
          renaming and deleting views all live in the section sidebar.
        -->
        <div class="flex flex-wrap items-center justify-end gap-2 px-3 py-2 border-t border-border/60">
          <div class="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              class="h-7 px-2 text-xs gap-1"
              type="button"
              data-testid="filter-reset-button"
              :disabled="isRunning"
              :loading="isRunning"
              @click="resetFilter"
            >
              <TrashIcon class="h-3.5 w-3.5" />
              Clear
            </Button>
            <Tooltip>
              <TooltipTrigger as-child>
                <Button
                  size="sm"
                  class="h-7 px-3 text-xs"
                  type="button"
                  data-testid="filter-run-button"
                  :disabled="isRunning"
                  :loading="isRunning"
                  @click="runFilter"
                >
                  Apply
                </Button>
              </TooltipTrigger>
              <TooltipContent>Apply filter to current data (⌘ + Enter)</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </PopoverContent>
    </Popover>
    <template #content>{{ iconOnlyTriggerTitle }}</template>
  </LazyTooltip>
</template>
